import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useApp } from '../context/AppContext';

const { FiArchive, FiDownload, FiEye, FiCalendar, FiBook, FiSearch, FiLock, FiFileText } = FiIcons;

const ArchivedReports = ({ archivedReports, onDownloadReport, onViewReport }) => {
  const { state, courseService } = useApp();
  const [reports, setReports] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [loading, setLoading] = useState(false);

  // Load archived reports from the database when component mounts
  useEffect(() => {
    loadArchivedReports();
  }, []);

  const loadArchivedReports = async () => {
    setLoading(true);
    try {
      const { data, error } = await courseService.getArchivedReports();
      if (error) throw new Error(error);
      if (data) {
        console.log("Loaded archived reports:", data.length);
        setReports(data);
      }
    } catch (error) {
      console.error("Error loading archived reports:", error);
    } finally {
      setLoading(false);
    }
  };

  const filteredReports = reports.filter(report => {
    const matchesSearch = report.courseName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         report.courseCode.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'all' || report.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

const handleViewCompleteReport = (report) => {
  if (report.report) {
    const reportWindow = window.open('', '_blank');
const totalStudents = report.report.gradeDistribution
  ? report.report.gradeDistribution.reduce((sum, grade) => sum + (grade.count || 0), 0)
  : 0;
      // Extract bell curve analysis data from the report
const bell = report.report.bellCurveAnalysis || {};
const total = bell.total || 1; // avoid division by zero

const actual = [
  bell.lowGrades || 0,
  bell.midGrades || 0,
  bell.highGrades || 0
];
// optimal as raw counts for comparison
const optimal = [
  Math.round(total * 0.15),
  Math.round(total * 0.7),
  Math.round(total * 0.15)
];
const labels = ['Below Avg', 'Average', 'High'];
const colors = ['#dc2626', '#2563eb', '#059669'];

const maxY = Math.max(...actual, ...optimal, 1);
const barWidth = 28;
const barGap = 32;
const chartHeight = 120;
const chartWidth = 2 * barGap + 3 * (barWidth * 2 + 4);

const barChartSection = `
  <div style="margin: 24px 0;">
    <div style="font-weight: bold; margin-bottom: 8px;">Bell Curve: Actual vs. Optimal</div>
    <svg width="${chartWidth}" height="${chartHeight + 30}">
      ${actual.map((val, i) => {
        // Bar positions
        const xActual = barGap + i * (barWidth * 2 + barGap);
        const xOptimal = xActual + barWidth + 4;

        // Heights
        const hActual = (val / maxY) * chartHeight;
        const hOptimal = (optimal[i] / maxY) * chartHeight;

        // Y positions
        const yActual = chartHeight - hActual + 20;
        const yOptimal = chartHeight - hOptimal + 20;

        return `
          <!-- Actual Bar -->
          <rect x="${xActual}" y="${yActual}" width="${barWidth}" height="${hActual}" fill="${colors[i]}" rx="3"/>
          <!-- Actual Label -->
          <text x="${xActual + barWidth/2}" y="${yActual - 6}" text-anchor="middle" font-size="12" fill="#333">${val}</text>
          <!-- Optimal Bar -->
          <rect x="${xOptimal}" y="${yOptimal}" width="${barWidth}" height="${hOptimal}" fill="#a3a3a3" rx="3"/>
          <!-- Optimal Label -->
          <text x="${xOptimal + barWidth/2}" y="${yOptimal - 6}" text-anchor="middle" font-size="12" fill="#333">${optimal[i]}</text>
          <!-- Category -->
          <text x="${xActual + barWidth + 2}" y="${chartHeight + 28}" text-anchor="middle" font-size="13" fill="#222">${labels[i]}</text>
        `;
      }).join('')}
      <!-- Legends -->
      <rect x="6" y="8" width="16" height="12" fill="#a3a3a3"/>
      <text x="26" y="18" font-size="12" fill="#222">Optimal</text>
      <rect x="80" y="8" width="16" height="12" fill="#2563eb"/>
      <text x="100" y="18" font-size="12" fill="#222">Actual</text>
    </svg>
    <div style="font-size: 12px; color: #666; margin-top: 4px;">Each group: left bar = actual, right bar = optimal</div>
  </div>
`;


    // Now you can use bellCurveSection in your full report HTML
    reportWindow.document.write(`
      <html>
<head>
  <title>Complete Report - ${report.courseCode}</title>
  <style>
    /* 1. Modern Font and Background */
    body {
      font-family: 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif;
      background: #f8fafc;
      color: #222;
      margin: 0;
      padding: 0;
    }
    /* 2. Card-like Report Container */
    .report-container {
      border-radius: 14px;
      box-shadow: 0 4px 24px rgba(16,30,54,0.09);
      background: #fff;
      padding: 36px 18px 60px 18px;
      margin: 35px auto;
      max-width: 940px;
      position: relative;
      z-index: 1;
    }
    /* 3. Watermark - Higher, More Subtle, Modern */
    .watermark {
      position: fixed;
      top: 13%;
      left: 50%;
      transform: translate(-50%, -50%);
      opacity: 0.09;
      font-size: 4rem;
      font-weight: 600;
      color: #007cba;
      z-index: 0;
      pointer-events: none;
      white-space: nowrap;
      user-select: none;
      letter-spacing: 0.12em;
    }
    /* 4. Header and Section Style */
    .header {
      border-bottom: 2px solid #e0e7ef;
      padding-bottom: 18px;
      margin-bottom: 26px;
    }
    h1 {
      margin: 0 0 8px 0;
      font-weight: 800;
      color: #1d4ed8;
      font-size: 2.5rem;
      letter-spacing: 0.03em;
    }
    h2 {
      margin-top: 0;
      font-size: 1.5rem;
      color: #1d4ed8;
      letter-spacing: 0.5px;
      border-left: 5px solid #1d4ed8;
      padding-left: 12px;
      background: linear-gradient(90deg,#e0e7ef 70%,#fff 100%);
      border-radius: 4px;
      font-weight: 700;
      margin-bottom: 18px;
    }
    h3 {
      font-size: 1.12rem;
      color: #334155;
      margin-bottom: 6px;
      font-weight: 600;
      border-bottom: 1px solid #e0e7ef;
      padding-bottom: 3px;
    }
    h4 {
      color: #2563eb;
      margin-bottom: 4px;
      margin-top: 18px;
      font-size: 1.02rem;
    }
    /* 5. Card Section */
    .section {
      margin-bottom: 36px;
      background: #fff;
      border-radius: 10px;
      box-shadow: 0 1px 5px rgba(16,30,54,0.07);
      padding: 24px 18px;
      page-break-inside: avoid;
    }
    /* 6. Modern Table */
    table {
      width: 100%;
      border-collapse: separate;
      border-spacing: 0;
      background: #f9fafb;
      border-radius: 8px;
      overflow: hidden;
      margin: 18px 0 4px 0;
      box-shadow: 0 1px 4px rgba(16,30,54,0.05);
    }
    th, td {
      padding: 11px 10px;
      text-align: center;
      font-size: 1.01em;
    }
    th {
      background: #1d4ed8;
      color: #fff;
      font-weight: 600;
      border-bottom: 2px solid #e0e7ef;
    }
    tr:nth-child(even) {
      background: #f1f5f9;
    }
    tr:hover {
      background: #e0e7ef;
    }
    td {
      border-bottom: 1px solid #e0e7ef;
    }
    /* 7. Badges and Statuses */
    .badge {
      display: inline-block;
      padding: 2px 10px;
      border-radius: 11px;
      font-size: 0.95em;
      background: #e0e7ef;
      color: #1d4ed8;
      margin-left: 4px;
      margin-right: 4px;
      font-weight: 500;
      letter-spacing: 0.03em;
    }
    .status-achieved {
      color: #16a34a;
      background-color: #dcfce7;
      padding: 4px 11px;
      border-radius: 5px;
      font-weight: 600;
    }
    .status-not-achieved {
      color: #dc2626;
      background-color: #fee2e2;
      padding: 4px 11px;
      border-radius: 5px;
      font-weight: 600;
    }
    /* 8. Analysis Boxes */
    .bell-curve-analysis {
      background-color: #f0f9ff;
      padding: 20px;
      border-radius: 8px;
      margin: 20px 0;
      box-shadow: 0 1px 4px rgba(16,30,54,0.07);
    }
    .clo-plo-mapping {
      background-color: #fef3c7;
      padding: 15px;
      border-radius: 8px;
      margin: 15px 0;
      box-shadow: 0 1px 4px rgba(16,30,54,0.04);
    }
    /* 9. Signature Section */
    .signature-section {
      margin-top: 35px;
    }
    .signature-box {
      width: 47%;
    }
    .signature-line {
      border-bottom: 1.5px solid #1d4ed8;
      height: 32px;
      margin-bottom: 6px;
      margin-top: 10px;
    }
    .signature-box h3 {
      margin-bottom: 10px;
      color: #2563eb;
      font-size: 1.1rem;
      font-weight: 700;
    }
    /* 10. Footer */
    .report-footer {
      border-top: 1px dashed #b9c3d1;
      margin-top: 32px;
      padding-top: 12px;
      font-size: 13px;
      color: #555;
      display: flex;
      justify-content: space-between;
      align-items: center;
      background: #fafbfc;
      z-index: 2;
      position: relative;
      border-radius: 0 0 10px 10px;
    }
    .no-print {
      margin-top: 28px;
      text-align: center;
    }
    .no-print button {
      padding: 15px 30px;
      background: #1d4ed8;
      color: white;
      border: none;
      border-radius: 6px;
      cursor: pointer;
      font-size: 16px;
      transition: background 0.18s;
      font-weight: 600;
    }
    .no-print button:hover {
      background: #2563eb;
    }
    /* 11. Responsive Design */
    @media (max-width: 700px) {
      .report-container, .section { padding: 12px 4px; }
      table, th, td { font-size: 0.95em; }
      h2 { font-size: 1.14rem; }
      h1 { font-size: 1.5rem; }
    }
    @media print { .no-print { display: none !important; } }
  </style>
</head>
<body>
  <div class="report-container">
    <div class="watermark">CLO-PLO Mapper</div>
            <div class="header">
              <h1>COMPREHENSIVE COURSE REPORT</h1>
              <h2>${report.report.courseInfo.courseName} (${report.report.courseInfo.courseCode})</h2>
              <div style="display: flex; justify-content: space-between; margin-top: 20px;">
                <div>
                  <p><strong>Faculty:</strong> ${report.report.courseInfo.faculty}</p>
                  <p><strong>Programme:</strong> ${report.programme}</p>
                  <p><strong>Semester:</strong> ${report.semester}</p>
                  <p><strong>Session:</strong> ${report.session}</p>
                </div>
                <div>
                  <p><strong>Credit Hours:</strong> ${report.report.courseInfo.creditHours}</p>
                  <p><strong>Report Generated:</strong> ${report.report.courseInfo.reportGeneratedDate}</p>
                  <p><strong>Generated By:</strong> ${report.report.courseInfo.reportGeneratedBy}</p>
                </div>
              </div>
            </div>
            <p style="font-style:italic; color:#007cba; margin-top:0; margin-bottom:24px;">
              Dear ${report.report.courseInfo.coordinatorName || "Course Coordinator"},<br>
              Please find below the comprehensive course analysis report.
            </p>
            <div class="section">
              <h2>1. ASSESSMENT PLAN</h2>
              <table>
                <tr>
                  <th>Assessment Type</th>
                  <th>Assessment Name</th>
                  <th>Marks (%)</th>
                  <th>CLO Mapping</th>
                  <th>CLO Statement</th>
                  <th>Mapped PLOs</th>
                </tr>
                ${report.report.assessmentPlan?.map(assessment => `
                  <tr>
                    <td>${assessment.type}</td>
                    <td>${assessment.name}</td>
                    <td>${assessment.marks}%</td>
                    <td>${assessment.clo}</td>
                    <td>${assessment.cloStatement}</td>
                    <td>${assessment.ploMappings?.map(plo => plo.code).join(', ') || 'None'}</td>
                  </tr>
                `).join('') || '<tr><td colspan="6">No assessment data available</td></tr>'}
              </table>
            </div>
            <div class="section">
              <h2>2. COURSE LEARNING OUTCOMES (CLO)</h2>
              <table>
                <tr><th>CLO</th><th>Statement</th></tr>
                ${Object.entries(report.report.cloStatements || {}).map(([clo, statement]) => `
                  <tr><td>${clo}</td><td>${statement}</td></tr>
                `).join('') || '<tr><td colspan="2">No CLO statements available</td></tr>'}
              </table>
            </div>
            <div class="section">
              <h2>3. PROGRAMME LEARNING OUTCOMES (PLO)</h2>
              <table>
                <tr><th>PLO</th><th>Statement</th></tr>
                ${Object.entries(report.report.ploStatements || {}).map(([plo, statement]) => `
                  <tr><td>${plo}</td><td>${statement}</td></tr>
                `).join('') || '<tr><td colspan="2">No PLO statements available</td></tr>'}
              </table>
            </div>
            <div class="section">
              <h2>4. CLO-PLO MAPPING</h2>
              <div class="clo-plo-mapping">
                ${report.report.enhancedCLOPLOMapping?.map(mapping => `
                  <div style="margin-bottom: 15px;">
                    <strong>${mapping.cloCode}:</strong> ${mapping.cloStatement}<br>
                    <span><strong>Mapped PLOs:</strong> ${mapping.mappedPLOs?.map(p => `${p.code} (${p.statement})`).join(', ') || 'None'}</span>
                  </div>
                `).join('') || '<div>No enhanced CLO-PLO mapping data available</div>'}
              </div>
            </div>
            <div class="section">
              <h2>5. STATISTICAL ANALYSIS</h2>
              <div style="display: flex;">
                <div style="flex:1">
                  <div style="margin: 10px;"><strong>Average Score:</strong> ${report.report.statistics?.averageScore || 0}%</div>
                  <div style="margin: 10px;"><strong>Highest Score:</strong> ${report.report.statistics?.highestScore || 0}%</div>
                  <div style="margin: 10px;"><strong>Lowest Score:</strong> ${report.report.statistics?.lowestScore || 0}%</div>
                </div>
              </div>
            </div>
            <div class="section">
              <h2>6. GRADE DISTRIBUTION</h2>
              <table>
                <tr><th>Grade</th><th>Range (%)</th><th>GPA</th><th>Count</th><th>Percentage</th></tr>
                ${report.report.gradeDistribution?.map(grade => `
                  <tr>
                    <td>${grade.grade}</td>
                    <td>${grade.min}-${grade.max}</td>
                    <td>${grade.gpa.toFixed(3)}</td>
                    <td>${grade.count}</td>
                    <td>${totalStudents ? ((grade.count / totalStudents) * 100).toFixed(1) : 0}%</td>
                  </tr>
                `).join('') || '<tr><td colspan="5">No grade distribution data available</td></tr>'}
              </table>
            </div>
           <div class="section">
  <h2>7. BELL CURVE DISTRIBUTION ANALYSIS</h2>
  <div class="bell-curve-analysis">
    <h3>Distribution Pattern: ${report.report.bellCurveAnalysis?.distributionPattern || 'Not available'}</h3>
    <div style="display: flex; justify-content: space-between; margin-top: 20px;">
      <div style="text-align: center;">
        <strong>High Achievers (A+, A, A-)</strong><br>
        <span style="font-size: 24px; color: #059669;">${report.report.bellCurveAnalysis?.highGrades || 0}</span><br>
        <span style="color: #059669;">${report.report.bellCurveAnalysis?.highPercentage || 0}% of students</span>
      </div>
      <div style="text-align: center;">
        <strong>Average Performers (B+, B, B-, C+, C)</strong><br>
        <span style="font-size: 24px; color: #2563eb;">${report.report.bellCurveAnalysis?.midGrades || 0}</span><br>
        <span style="color: #2563eb;">${report.report.bellCurveAnalysis?.midPercentage || 0}% of students</span>
      </div>
      <div style="text-align: center;">
        <strong>Below Average (C-, D, F)</strong><br>
        <span style="font-size: 24px; color: #dc2626;">${report.report.bellCurveAnalysis?.lowGrades || 0}</span><br>
        <span style="color: #dc2626;">${report.report.bellCurveAnalysis?.lowPercentage || 0}% of students</span>
      </div>
    </div>

    <!-- SVG BAR CHART: Actual vs Optimal -->
    <div style="margin: 32px 0 8px 0; display: flex; flex-direction: column; align-items: center;">
      <div style="font-weight: bold; margin-bottom: 8px;">Bell Curve: Actual vs. Optimal</div>
      <svg width="320" height="150">
        ${(() => {
          // Data extraction
          const bell = report.report.bellCurveAnalysis || {};
          const total = bell.total || (
            (bell.highGrades || 0) + (bell.midGrades || 0) + (bell.lowGrades || 0)
          ) || 1; // avoid division by zero

          const actual = [
            bell.lowGrades || 0,
            bell.midGrades || 0,
            bell.highGrades || 0
          ];
          const labels = ['Below Avg', 'Average', 'High'];
          const colors = ['#dc2626', '#2563eb', '#059669'];
          // Optimal as per 15/70/15% split
          const optimal = [
            Math.round(total * 0.15),
            Math.round(total * 0.7),
            Math.round(total * 0.15)
          ];
          const maxY = Math.max(...actual, ...optimal, 1);
          const barWidth = 26, barGap = 32;
          // SVG bars
          return actual.map((val, i) => {
            const xActual = barGap + i * (barWidth * 2 + barGap);
            const xOptimal = xActual + barWidth + 4;
            const hActual = (val / maxY) * 90;
            const hOptimal = (optimal[i] / maxY) * 90;
            const yActual = 120 - hActual;
            const yOptimal = 120 - hOptimal;
            return `
              <!-- Actual Bar -->
              <rect x="${xActual}" y="${yActual}" width="${barWidth}" height="${hActual}" fill="${colors[i]}" rx="3"/>
              <text x="${xActual + barWidth/2}" y="${yActual - 6}" text-anchor="middle" font-size="12" fill="#222">${val}</text>
              <!-- Optimal Bar -->
              <rect x="${xOptimal}" y="${yOptimal}" width="${barWidth}" height="${hOptimal}" fill="#a3a3a3" rx="3"/>
              <text x="${xOptimal + barWidth/2}" y="${yOptimal - 6}" text-anchor="middle" font-size="12" fill="#222">${optimal[i]}</text>
              <!-- Label -->
              <text x="${xActual + barWidth + 2}" y="142" text-anchor="middle" font-size="13" fill="#222">${labels[i]}</text>
            `;
          }).join('') +
          `
          <!-- Legend -->
          <rect x="8" y="8" width="16" height="12" fill="#a3a3a3"/>
          <text x="28" y="18" font-size="12" fill="#222">Optimal</text>
          <rect x="82" y="8" width="16" height="12" fill="#2563eb"/>
          <text x="102" y="18" font-size="12" fill="#222">Actual</text>
          `
        })()}
      </svg>
      <div style="font-size: 12px; color: #666; margin-top: 4px;">Each group: left bar = actual, right bar = optimal</div>
    </div>
    <!-- END SVG BAR CHART -->
  </div>
</div>
            <div class="section">
              <h2>8. CLO ACHIEVEMENT ANALYSIS</h2>
              <table>
                <tr>
                  <th>CLO</th>
                  <th>CLO Statement</th>
                  <th>Target (%)</th>
                  <th>Achievement (%)</th>
                  <th>Students Achieved</th>
                  <th>Status</th>
                </tr>
                ${report.report.cloAchievementData?.map(clo => `
                  <tr>
                    <td>${clo.clo}</td>
                    <td>${clo.statement}</td>
                    <td>${clo.targetPercentage}%</td>
                    <td>${clo.attainment}%</td>
                    <td>${clo.studentsAchieved}/${clo.totalStudents}</td>
                    <td><span class="${clo.status === 'Achieved' ? 'status-achieved' : 'status-not-achieved'}">${clo.status}</span></td>
                  </tr>
                `).join('') || '<tr><td colspan="6">No CLO achievement data available</td></tr>'}
              </table>
            </div>
            <div class="section">
              <h2>9. PLO ACHIEVEMENT ANALYSIS</h2>
              <h3>Mapped PLOs</h3>
              <table>
                <tr>
                  <th>PLO</th>
                  <th>PLO Statement</th>
                  <th>Target (%)</th>
                  <th>Achievement (%)</th>
                  <th>Students Achieved</th>
                  <th>Status</th>
                </tr>
                ${report.report.ploAchievementData?.mappedPLOs?.map(plo => `
                  <tr>
                    <td>${plo.plo}</td>
                    <td>${plo.description}</td>
                    <td>${plo.targetPercentage}%</td>
                    <td>${plo.attainment}%</td>
                    <td>${plo.studentsAchieved}/${plo.totalStudents}</td>
                    <td><span class="${plo.status === 'Achieved' ? 'status-achieved' : 'status-not-achieved'}">${plo.status}</span></td>
                  </tr>
                `).join('') || '<tr><td colspan="6">No mapped PLO achievement data available</td></tr>'}
              </table>
            </div>
            <div class="section">
              <h2>10. FAILURE REPORT</h2>
              ${report.report.failureReport?.length ? `
                <table>
                  <tr><th>Student Name</th><th>Matric No</th><th>Status</th><th>Reason</th></tr>
                  ${report.report.failureReport.map(failure => `
                    <tr>
                      <td>${failure.studentName}</td>
                      <td>${failure.matricNo}</td>
                      <td>${failure.failureStatus}</td>
                      <td>${failure.reason}</td>
                    </tr>
                  `).join('')}
                </table>
              ` : '<div>No failure report data available.</div>'}
            </div>
            <div class="section">
              <h2>11. ANALYSIS COMMENTS</h2>
              <h3>Course Performance Analysis</h3>
              <p>${report.report.coursePerformance?.comments || 'No comments provided'}</p>
              <h4>Suggestions for Improvement:</h4>
              <ul>
                ${report.report.coursePerformance?.suggestions?.map(suggestion => `<li>${suggestion}</li>`).join('') || '<li>No suggestions provided</li>'}
              </ul>
              <h3>CLO Analysis</h3>
              <p>${report.report.cloAnalysis?.comments || 'No comments provided'}</p>
              <h4>Suggestions for Improvement:</h4>
              <ul>
                ${report.report.cloAnalysis?.suggestions?.map(suggestion => `<li>${suggestion}</li>`).join('') || '<li>No suggestions provided</li>'}
              </ul>
              <h3>PLO Analysis</h3>
              <p>${report.report.ploAnalysis?.comments || 'No comments provided'}</p>
              <h4>Suggestions for Improvement:</h4>
              <ul>
                ${report.report.ploAnalysis?.suggestions?.map(suggestion => `<li>${suggestion}</li>`).join('') || '<li>No suggestions provided</li>'}
              </ul>
            </div>
            <div class="signature-section">
              <h2>12. SIGNATURES</h2>
              <div style="display: flex; justify-content: space-between;">
                <div className="signature-box">
                  <h3>Course Coordinator</h3>
                  <div className="signature-line"></div>
                  <p><strong>Name:</strong> ${report.report.signatures?.courseCoordinator?.name || '_________________'}</p>
                  <p><strong>Position:</strong> ${report.report.signatures?.courseCoordinator?.position || 'Course Coordinator'}</p>
                  <p><strong>Faculty:</strong> ${report.report.signatures?.courseCoordinator?.faculty || '_________________'}</p>
                  <p><strong>Department:</strong> ${report.report.signatures?.courseCoordinator?.department || '_________________'}</p>
                  <p><strong>Date:</strong> ${report.report.signatures?.courseCoordinator?.signatureDate || '_________________'}</p>
                </div>
                <div class="signature-box">
                  <h3>Programme Coordinator</h3>
                  <div class="signature-line"></div>
                  <p><strong>Name:</strong> ${report.report.signatures?.programmeCoordinator?.name || '_________________'}</p>
                  <p><strong>Position:</strong> ${report.report.signatures?.programmeCoordinator?.position || 'Programme Coordinator'}</p>
                  <p><strong>Faculty:</strong> ${report.report.signatures?.programmeCoordinator?.faculty || '_________________'}</p>
                  <p><strong>Department:</strong> ${report.report.signatures?.programmeCoordinator?.department || '_________________'}</p>
                  <p><strong>Date:</strong> ${report.report.signatures?.programmeCoordinator?.signatureDate || '_________________'}</p>
                </div>
              </div>
            </div>
            <div class="report-footer">
              <span>Generated: ${new Date().toLocaleString()}</span>
              <span>Generated using CLO-PLO Mapper by FAHIM</span>
            </div>
            <div class="no-print" style="margin-top: 30px; text-align: center;">
              <button onclick="window.print()" style="padding: 15px 30px; background: #007cba; color: white; border: none; border-radius: 5px; cursor: pointer; font-size: 16px;">Print Report</button>
            </div>
          </div>
        </body>
      </html>
    `);
    reportWindow.document.close();
  } else {
    alert('Complete report data not available for this course.');
  }
};

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Archived Reports</h2>
          <div className="flex items-center space-x-4">
            <div className="relative">
              <SafeIcon icon={FiSearch} className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search reports..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Status</option>
              <option value="completed">Completed</option>
              <option value="finalized">Finalized</option>
              <option value="archived">Archived</option>
            </select>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-8">
            <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">Loading archived reports...</p>
          </div>
        ) : filteredReports.length === 0 ? (
          <div className="text-center py-8">
            <SafeIcon icon={FiArchive} className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">No archived reports found.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse border border-gray-300">
              <thead>
                <tr className="bg-gray-50">
                  <th className="border border-gray-300 px-4 py-2 text-left">Course</th>
                  <th className="border border-gray-300 px-4 py-2 text-left">Programme</th>
                  <th className="border border-gray-300 px-4 py-2 text-left">Faculty</th>
                  <th className="border border-gray-300 px-4 py-2 text-left">Semester/Session</th>
                  <th className="border border-gray-300 px-4 py-2 text-left">Completed Date</th>
                  <th className="border border-gray-300 px-4 py-2 text-left">Status</th>
                  <th className="border border-gray-300 px-4 py-2 text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredReports.map((report) => (
                  <tr key={report.id} className="hover:bg-gray-50">
                    <td className="border border-gray-300 px-4 py-2">
                      <div className="flex items-center space-x-3">
                        <div className="bg-blue-100 p-2 rounded-lg">
                          <SafeIcon icon={FiBook} className="w-4 h-4 text-blue-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{report.courseName}</p>
                          <p className="text-sm text-gray-600">{report.courseCode}</p>
                        </div>
                      </div>
                    </td>
                    <td className="border border-gray-300 px-4 py-2">
                      <p className="text-sm text-gray-900">{report.programme}</p>
                    </td>
                    <td className="border border-gray-300 px-4 py-2">
                      <p className="text-sm text-gray-900">{report.faculty}</p>
                    </td>
                    <td className="border border-gray-300 px-4 py-2">
                      <div className="space-y-1">
                        <span className="inline-block text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                          {report.semester}
                        </span>
                        <br />
                        <span className="inline-block text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                          {report.session}
                        </span>
                      </div>
                    </td>
                    <td className="border border-gray-300 px-4 py-2">
                      <div className="flex items-center space-x-2">
                        <SafeIcon icon={FiCalendar} className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-600">
                          {formatDate(report.completedAt)}
                        </span>
                      </div>
                    </td>
                    <td className="border border-gray-300 px-4 py-2">
                      <span className={`text-xs px-2 py-1 rounded-full flex items-center space-x-1 ${
                        report.status === 'finalized' 
                          ? 'bg-purple-100 text-purple-800' 
                          : report.status === 'completed' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {report.status === 'finalized' && <SafeIcon icon={FiLock} className="w-3 h-3" />}
                        <span>{report.status}</span>
                      </span>
                    </td>
                    <td className="border border-gray-300 px-4 py-2">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => onViewReport(report)}
                          className="p-1 text-blue-600 hover:text-blue-800"
                          title="View Report Summary"
                        >
                          <SafeIcon icon={FiEye} className="w-4 h-4" />
                        </button>
                        {report.status === 'finalized' && (
                          <button
                            onClick={() => handleViewCompleteReport(report)}
                            className="p-1 text-purple-600 hover:text-purple-800"
                            title="View Complete Report"
                          >
                            <SafeIcon icon={FiFileText} className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => onDownloadReport(report, 'pdf')}
                          className="p-1 text-green-600 hover:text-green-800"
                          title="Download PDF"
                        >
                          <SafeIcon icon={FiDownload} className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Legend */}
        <div className="mt-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
          <h4 className="font-semibold text-gray-900 mb-2">Status Legend:</h4>
          <div className="flex flex-wrap gap-4 text-sm">
            <div className="flex items-center space-x-2">
              <span className="w-3 h-3 bg-green-500 rounded-full"></span>
              <span className="text-gray-700">Completed - Report generated but not finalized</span>
            </div>
            <div className="flex items-center space-x-2">
              <SafeIcon icon={FiLock} className="w-3 h-3 text-purple-600" />
              <span className="text-gray-700">Finalized - Report transmitted and locked (no further edits)</span>
            </div>
            <div className="flex items-center space-x-2">
              <span className="w-3 h-3 bg-blue-500 rounded-full"></span>
              <span className="text-gray-700">Archived - Stored for historical reference</span>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default ArchivedReports;